import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, X, ChevronDown } from 'lucide-react';
import Footer from '../components/Footer';
import '../styles/eyestrain-clean.css';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button 
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <ChevronDown className={`faq-icon ${isOpen ? 'rotate' : ''}`} />
      </button>
      {isOpen && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const EyeStrainRelief = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 21,
    minutes: 9,
    seconds: 31
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { days, hours, minutes, seconds } = prevTime;
        
        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <div className="advertorial-page eyestrain-page">
      <Helmet>
        <title>Eye Strain Relief - Why Eye Drops Aren't Working & What Women Are Switching To</title>
        <meta 
          name="description" 
          content="Eye drops aren't solving eye strain. Discover why women with dry, tired eyes are switching to the Ocular Relax Reset™ method for lasting relief in 10-15 minutes." 
        />
        <meta 
          name="keywords" 
          content="eye strain relief, dry eyes, tired eyes, eye drops alternative, screen fatigue, digital eye strain, eye mask, warm compress, spa eye treatment" 
        />
        <meta property="og:title" content="Eye Strain Relief - Why Eye Drops Aren't Working & What Women Are Switching To" />
        <meta 
          property="og:description" 
          content="Discover the Ocular Relax Reset™ method that's helping women get lasting relief from eye strain and dry eyes." 
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eye Strain Relief - Why Eye Drops Aren't Working" />
        <meta 
          name="twitter:description" 
          content="The new method for dry, tired eyes that works in 10-15 minutes." 
        />
        <link rel="canonical" href="https://yourdomain.com/eye-strain-relief" />
      </Helmet>

      {/* Main Content */}
      <div className="main-content">
        {/* Trending Badge */}
        <div className="trending-badge">
          ⭐ Trending in the US
        </div>

        {/* Hero Section */}
        <section className="hero-section">
          <h1 className="hero-title eyestrain-title">
            Eye drops aren't solving eye strain — here's why women with dry, tired eyes are switching to the <span className="hero-highlight">Ocular Relax Reset™ method</span>
          </h1>
          
          <div className="author-info">
            <p className="author-name">By Jennifer K.</p>
            <p className="publish-date">Last Updated December 2025</p>
          </div>

          <div className="update-notice">
            <strong>UPDATE:</strong> Selling out faster than expected — Early Holiday Allocation still available.
          </div>
        </section>

        {/* Wide Hero Image */}
        <section className="wide-image-section">
          <div className="wide-image-placeholder">
           
             <img 
              src="/images/WO_Grid-1b.jpg" 
              alt="Woman with eye strain using Kickin'On steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        {/* Reason 1 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason1.jpg" 
              alt="Woman with eye strain using Kickin'On steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">1. It Targets Eye Strain at the Source — Not Just the Dryness</h2>
            <p className="feature-description">
              Most women searching "eye strain relief," "best eye drops," or "dry eyes" have no idea the real problem isn't always lack of moisture…
            </p>
            <p className="feature-description emphasis-text">
              It's tension.
            </p>
            <p className="feature-description">
              Screen time makes adults blink 66% less, causing muscle strain AND faster tear evaporation.
            </p>
            <p className="feature-description">
              Artificial tears feel soothing, but the relief fades in minutes because the strain underneath is still there.
            </p>
            <p className="feature-description">
              Kickin'On uses gentle, consistent warm steam to relax the overworked muscles around the eyes — 
              helping dryness and strain at the same time.
            </p>
            <p className="feature-quote">
              Women describe it as "melting the pressure away."
            </p>
          </div>
        </section>

        {/* Reason 2 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason6.jpg" 
              alt="Spa-quality eye treatment at home" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">2. It Feels Like an $150 Spa Eye Treatment — But Costs Less Than a Coffee</h2>
            <p className="feature-description">
              Spa "eye rejuvenation" services often start at $120–$180 per session.
            </p>
            <p className="feature-description">
              They work because they use controlled warmth to help soften the eye area and restore comfort.
            </p>
            <p className="feature-description">
              Kickin'On does the same thing — but in a portable mask you can use anywhere.
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> No cords</li>
              <li><Check className="check-icon" /> No appointments</li>
              <li><Check className="check-icon" /> No heated towels</li>
              <li><Check className="check-icon" /> No microwaving</li>
            </ul>
            <p className="feature-description">
              Just spa-grade calm in 10 seconds.
            </p>
            <p className="feature-description">
              Slip it into your handbag, laptop bag, or carry-on.
            </p>
          </div>
        </section>

        {/* Reason 3 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason4.jpg" 
              alt="Hygienic single-use steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">3. Clean, Gentle Steam — Not Reheated Bacteria or Harsh Heat</h2>
            <p className="feature-description">
              Most women try microwaved eye masks before realizing the problem:
            </p>
            <p className="feature-description">
              They absorb oils, sweat, makeup, and bacteria… and people keep reheating them.
            </p>
            <p className="feature-description highlight-text">
              A Food Control study found 747 strains of bacteria inside household microwaves.
            </p>
            <p className="feature-description">
              Many survive heat cycles.
            </p>
            <p className="feature-description">
              That warmed residue goes right onto your eyelids — the thinnest skin on the body.
            </p>
            <p className="feature-description">
              Kickin'On is:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Single-use</li>
              <li><Check className="check-icon" /> Hygienic</li>
              <li><Check className="check-icon" /> Evenly heated</li>
              <li><Check className="check-icon" /> Fresh every time</li>
            </ul>
            <p className="feature-description">
              Perfect for sensitive eyes.
            </p>
          </div>
        </section>

        {/* Reason 4 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason7.jpg" 
              alt="Quick and simple eye strain relief" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">4. Simple, Quick, and Works in Minutes</h2>
            <p className="feature-description">
              The Ocular Relax Reset™ method is effortless:
            </p>
            <p className="feature-description emphasis-text">
              Open → activate → unwind.
            </p>
            <p className="feature-description">
              Most women report:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Burning easing</li>
              <li><Check className="check-icon" /> Strain melting</li>
              <li><Check className="check-icon" /> Less pressure behind the eyes</li>
              <li><Check className="check-icon" /> Reduced drop-dependency</li>
              <li><Check className="check-icon" /> Calmer evenings</li>
            </ul>
            <p className="feature-description">
              Within 10 minutes.
            </p>
            <p className="feature-description">
              No batteries. No cords. No reheating.
            </p>
            <p className="feature-description">
              Just clean, comforting steam anywhere you need it.
            </p>
          </div>
        </section>

        {/* Reason 5 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason2.jpg" 
              alt="Reduce eye drop dependency" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">5. Helps Reduce the "Dryness Cycle" Caused by Constant Eye Drops</h2>
            <p className="feature-description">
              Artificial tears can help — but many women experience the "rebound dryness loop":
            </p>
            <p className="feature-description emphasis-text">
              Dry → drops → relief → dry again.
            </p>
            <p className="feature-description">
              Drops only replace water. They don't calm the tension or slow evaporation.
            </p>
            <p className="feature-description">
              Warm steam helps:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Soften the eye area</li>
              <li><Check className="check-icon" /> Support natural moisture</li>
              <li><Check className="check-icon" /> Relax strain</li>
              <li><Check className="check-icon" /> Improve comfort for hours, not minutes</li>
            </ul>
            <p className="feature-description">
              So many women report they reach for drops less often after adding steam into their routine.
            </p>
          </div>
        </section>

        {/* Reason 6 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason3.jpg" 
              alt="Look more awake and refreshed" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">6. A Daily Ritual That Helps You Look More Awake, Confident, and Put-Together</h2>
            <p className="feature-description">
              Women 25+ often notice that eye strain doesn't just hurt — it shows.
            </p>
            <ul className="feature-list negative">
              <li>Puffy lids</li>
              <li>Tightness</li>
              <li>Redness</li>
              <li>Tired expression</li>
              <li>Makeup settling into fine lines</li>
            </ul>
            <p className="feature-description">
              When the eye area relaxes and rehydrates, everything looks softer, brighter, and calmer.
            </p>
            <p className="feature-description emphasis-text">
              Your eyes set the tone of your whole face — and Kickin'On helps restore that refreshed, composed appearance.
            </p>
          </div>
        </section>

        {/* Reason 7 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason5.jpg" 
              alt="Designed for the digital era" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">7. Designed for the Digital Era — Not For the World Before Screens</h2>
            <p className="feature-description">
              Older warm compresses were invented for a different time.
            </p>
            <p className="feature-description">
              Kickin'On was built for:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Phone fatigue</li>
              <li><Check className="check-icon" /> Laptop marathons</li>
              <li><Check className="check-icon" /> Late-night scrolling</li>
              <li><Check className="check-icon" /> Zoom-heavy careers</li>
              <li><Check className="check-icon" /> Air-conditioned offices</li>
              <li><Check className="check-icon" /> Sleep disrupted by screens</li>
              <li><Check className="check-icon" /> Stress</li>
            </ul>
            <p className="feature-description emphasis-text">
              If your eyes feel like they're carrying your whole day… this is the method designed to give them relief.
            </p>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="reviews-section">
          <h2 className="reviews-title">What Women Are Saying</h2>
          <p className="reviews-subtitle">Over 10,000+ five-star reviews from women who switched to Kickin'On</p>
          
          <div className="reviews-grid">
            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Sarah M.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "I work on screens 8+ hours a day and my eyes would burn by 3pm. These masks are a game changer. 
                The warmth melts the tension away and I actually feel like I can focus again. No more reaching for eye drops every hour!"
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Jessica L.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "Finally something that actually works! I've tried heated masks before but they were either too hot or didn't stay warm. 
                Kickin'On is perfect every time. My eyes look brighter and I don't have that tired, strained look anymore."
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Amanda K.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "I was skeptical but desperate. After one use, I was hooked. The gentle steam is so soothing and it helps way more than drops. 
                I use one before bed and wake up with comfortable eyes. Worth every penny!"
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Michelle R.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "These are perfect for travel! I use them on flights and they're a lifesaver. The dryness from cabin air used to ruin my eyes for days. 
                Now I just use a Kickin'On mask and arrive feeling refreshed."
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Lauren T.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "My eye doctor recommended warm compresses but microwaving a mask felt gross. This is so much cleaner and easier. 
                My meibomian gland dysfunction has improved so much. Highly recommend!"
              </p>
            </div>

            <div className="review-card">
              <div className="review-header">
                <span className="review-author">Rachel W.</span>
                <span className="review-stars">★★★★★</span>
              </div>
              <div className="review-verified">✓ Verified Purchase</div>
              <p className="review-text">
                "I keep these in my desk drawer at work. When my eyes start feeling tired, I take a 15-minute break with one of these and I'm good to go. 
                It's become my secret weapon for long workdays!"
              </p>
            </div>
          </div>
        </section>

     {/* Timeline Section */}
<section className="timeline-section">
  <h2 className="timeline-title">Your Kickin’On Eye Steam Mask Transformation Timeline</h2>

  <p className="timeline-subtitle">
    While most people feel relief within minutes, the Ocular Relax Reset™ method helps your eyes unwind, rehydrate, and recover from daily strain over time. Here’s what you can expect 👇
  </p>

  <div className="timeline-grid">
    {/* Week 1–2 */}
    <div className="timeline-item">
      <div className="timeline-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
          <circle cx="20" cy="20" r="12" fill="#88E1C1" />
        </svg>
      </div>
      <div className="timeline-content">
        <h3 className="timeline-period">Week 1–2: Instant Relaxation</h3>
        <p className="timeline-description">
          Feel quick relief from eye strain, tightness, and screen fatigue. Gentle steam softens the eye area and helps your eyes finally relax.
        </p>
      </div>
    </div>

    {/* Week 3–4 */}
    <div className="timeline-item">
      <div className="timeline-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
          <circle cx="20" cy="20" r="12" fill="#88E1C1" />
        </svg>
      </div>
      <div className="timeline-content">
        <h3 className="timeline-period">Week 3–4: Hydration & Brightness Returns</h3>
        <p className="timeline-description">
          Natural moisture balance improves as steam helps slow evaporation. Eyes look fresher, less tired, and more awake—even after long days.
        </p>
      </div>
    </div>

    {/* Month 2 */}
    <div className="timeline-item">
      <div className="timeline-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
          <circle cx="20" cy="20" r="12" fill="#88E1C1" />
        </svg>
      </div>
      <div className="timeline-content">
        <h3 className="timeline-period">Month 2: Deep Comfort & Less Drop Dependency</h3>
        <p className="timeline-description">
          As your eyes stay more comfortable for longer, many people find they reach for artificial tears far less often. Calm becomes the new normal.
        </p>
      </div>
    </div>

    {/* Month 3 */}
    <div className="timeline-item">
      <div className="timeline-icon">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="18" fill="#88E1C1" opacity="0.25" />
          <circle cx="20" cy="20" r="12" fill="#88E1C1" />
        </svg>
      </div>
      <div className="timeline-content">
        <h3 className="timeline-period">Month 3: Long-Term Ease & Morning-Ready Eyes</h3>
        <p className="timeline-description">
          Consistent steam sessions help your eyes stay relaxed, hydrated, and less reactive to screens. Mornings feel easier. Evenings feel calmer.
        </p>
      </div>
    </div>
  </div>

  <div className="timeline-cta">
  <a 
  href="https://kickinon.com/products/kickinon-steam-eye-mask" 
  className="timeline-cta-button"
>
  👉 TRY KICKIN’ON STEAM EYE MASK RISK-FREE
</a>
  </div>
</section>


      {/* Comparison Table */}
<section className="comparison-section">
  <h2 className="comparison-title">How Kickin’On Steam Eye Mask Stands Apart</h2>
  <p className="comparison-subtitle">
    Most eye treatments only cool or add temporary moisture. Kickin’On uses <strong>clean, gentle steam</strong> to relax strained eyes and support natural moisture in a way drops alone can’t 👇
  </p>

  <div className="comparison-table-wrapper">
    <table className="comparison-table">
      <thead>
        <tr>
          <th className="product-col">
            <div className="product-icon-placeholder">
  <img src="/images/offer-section.jpg" alt="Kickin’On Icon" className="product-icon-img" />
</div>
          </th>
          <th className="feature-col">Feature</th>
          <th className="competitors-col">Eye Drops</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Gentle Moist Steam (Ocular Relax Reset™)</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>

        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Relieves Eye Strain & Tension</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>

        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Supports Natural Moisture (Not Just Temporary Wetness)</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>

        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Single-Use, Hygienic & Clean</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>

        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Cordless, Travel-Ready — No Prep, No Microwave</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>

        <tr>
          <td className="product-col">
            <Check className="icon-check-green" />
          </td>
          <td className="feature-col">Consistent Heat for 20+ Minutes</td>
          <td className="competitors-col"><X className="icon-x-red" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</section>


     {/* Final Message */}
<section className="final-message">
  <h2 className="final-message-title">
    Reclaim Calm, Clarity & Comfortable Eyes with <span className="brand-highlight">Kickin’On</span>
  </h2>

  <div className="final-message-intro">
    <p>
      If you're tired of waking up with tired, puffy, or strained eyes, struggling through long screen hours, or wishing you could feel calm and refreshed again — it's time to give your eyes the gentle care they've been missing.
    </p>

    <p>
      <strong>Kickin’On Steam Eye Mask</strong> uses the Ocular Relax Reset™ method to melt tension, ease dryness from fast tear evaporation, and relax overworked eye muscles — naturally.
    </p>

    <p>No microwaves. No cords. No irritation. Just warm, clean steam that feels like a tiny spa moment — anywhere.</p>

    <p>
      One mask a day helps unwind your mind, soothe eye strain, and bring back brighter, calmer, more comfortable eyes every morning.
    </p>
  </div>

  <div className="photo-grid-container">
    <div className="photo-grid">
      <div className="photo-item"><div className="photo-placeholder">👁️</div></div>
      <div className="photo-item"><div className="photo-placeholder">😌</div></div>
      <div className="photo-item"><div className="photo-placeholder">✨</div></div>
      <div className="photo-item"><div className="photo-placeholder">🌿</div></div>
      
      <div className="photo-item photo-center">
        <div className="guarantee-badge">
          <div className="badge-stars">★ ★ ★ ★ ★</div>
          <div className="badge-seal">
            <div className="badge-title">GUARANTEE</div>
            <div className="badge-percentage">100%</div>
            <div className="badge-subtitle">MONEY BACK</div>
          </div>
          <div className="badge-rating">rated 4.7/5</div>
        </div>
      </div>

      <div className="photo-item"><div className="photo-placeholder">💤</div></div>
      <div className="photo-item"><div className="photo-placeholder">😊</div></div>
      <div className="photo-item"><div className="photo-placeholder">💚</div></div>
      <div className="photo-item"><div className="photo-placeholder">🌟</div></div>
    </div>
  </div>

  <ul className="final-benefits-list">
    <li><Check className="benefit-check" /> Gentle Steam for Fast Eye Strain Relief</li>
    <li><Check className="benefit-check" /> Softens Dryness & Reduces Puffiness</li>
    <li><Check className="benefit-check" /> Supports Calm, Relaxation & Better Rest</li>
    <li><Check className="benefit-check" /> Fresh, Single-Use & Hygienic</li>
    <li><Check className="benefit-check" /> Cord-Free, Travel-Friendly & Ready Anywhere</li>
  </ul>

  <p className="final-message-closing">Your comfort matters. Your calm matters. And your eyes deserve a break.</p>

<a style={{ textAlign: "center" }} 
  href="https://kickinon.com/products/kickinon-steam-eye-mask" 
  className="final-message-cta"
>
  👉 TRY KICKIN’ON STEAM EYE MASK RISK-FREE
</a>
</section>

        {/* Final CTA */}
        <section className="final-cta-section">
          <div className="cta-two-column">
            <div className="cta-image-column">
              <img 
                src="/images/offer-section.jpg" 
                alt="Kickin'On Steam Eye Mask - Buy 2 Get 1 Free" 
                className="cta-offer-image"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="cta-content-column">
              <h3 className="final-cta-title">👉 Try Kickin'On Steam Eye Mask</h3>
              <p className="final-cta-subtitle">The Ocular Relax Reset™ Method</p>
              <div className="final-cta-badge">Buy 2 Get 1 FREE — Limited Time Only</div>
              <a 
                href="https://kickinon.com/products/kickinon-steam-eye-mask" 
                target="_blank" 
                rel="noopener noreferrer"
                className="final-cta-button"
              >
                GET YOUR EYE STRAIN RELIEF NOW
              </a>
              <p className="final-guarantee">30-Day Money Back Guarantee</p>
            </div>
          </div>
        </section>

{/* FAQ Section */}
<section className="faq-section">
  <h2 className="faq-title">Frequently Asked Questions</h2>
  
  <div className="faq-container">
    
    <FAQItem
      question="How long does each Kickin’On Steam Eye Mask last?"
      answer="Each Kickin’On mask provides a consistent 20–25 minutes of clean, gentle steam. This is the ideal window to relax tight eye muscles, ease strain from screens, and help your eyes naturally rehydrate without irritation or microwaving anything."
    />

    <FAQItem
      question="Is it safe to use every day?"
      answer="Yes — Kickin’On masks are designed for daily use and are safe for sensitive skin. The Ocular Relax Reset™ method uses controlled warmth and moisture to calm your eyes naturally, without chemicals, electricity, or reheated bacteria."
    />

    <FAQItem
      question="Can I use it if I wear contact lenses?"
      answer="For best comfort, we recommend removing your contact lenses before using the mask. The warm steam helps restore moisture on the surface of the eye, which is especially beneficial for people who experience dryness from contacts. You can reinsert your lenses afterward."
    />

    <FAQItem
      question="What makes this better than eye drops?"
      answer="Eye drops only add temporary moisture — often for minutes. Kickin’On supports natural moisture by relaxing the eyelids, softening the tear film, and reducing rapid evaporation caused by screens and dry environments. It targets the comfort issue drops can't fix."
    />

    <FAQItem
      question="Can I take Kickin’On when traveling?"
      answer="Absolutely. Because each mask is single-use, clean, cordless, and TSA-friendly, it’s perfect for planes, hotels, or long workdays. Many customers rely on it for air travel, where dry cabin air quickly strains the eyes."
    />
  </div>
</section>

      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EyeStrainRelief;
